import { useState, useEffect, useCallback, useRef } from "react";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import {
  BookMarked, Search, Plus, Pin, PinOff, Trash2, Pencil, ExternalLink, Download,
  FileText, FileSpreadsheet, FileImage, File as FileIcon, Video, Link as LinkIcon,
  Upload, X, Save, Eye, Tag,
} from "lucide-react";

export const LIBRARY_CATEGORIES = [
  { value: "sop",         label: "SOP" },
  { value: "guide",       label: "Guide" },
  { value: "onboarding",  label: "Onboarding" },
  { value: "curriculum",  label: "Curriculum" },
  { value: "form",        label: "Form" },
  { value: "training",    label: "Training" },
  { value: "reference",   label: "Reference" },
  { value: "other",       label: "Other" },
];

const CATEGORY_COLORS = {
  sop:        "bg-rose-100 text-rose-700 border-rose-200",
  guide:      "bg-blue-100 text-blue-700 border-blue-200",
  onboarding: "bg-emerald-100 text-emerald-700 border-emerald-200",
  curriculum: "bg-violet-100 text-violet-700 border-violet-200",
  form:       "bg-amber-100 text-amber-700 border-amber-200",
  training:   "bg-sky-100 text-sky-700 border-sky-200",
  reference:  "bg-slate-100 text-slate-700 border-slate-200",
  other:      "bg-slate-100 text-slate-600 border-slate-200",
};

const ItemIcon = ({ item, className }) => {
  if (item.kind === "link") return <LinkIcon className={className} />;
  const ext = (item.file_extension || "").toLowerCase();
  if (ext === "pdf") return <FileText className={className} />;
  if (["docx", "doc", "txt", "md"].includes(ext)) return <FileText className={className} />;
  if (["xlsx", "xls"].includes(ext)) return <FileSpreadsheet className={className} />;
  if (["pptx", "ppt"].includes(ext)) return <FileText className={className} />;
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return <FileImage className={className} />;
  if (["mp4", "mov", "webm"].includes(ext)) return <Video className={className} />;
  return <FileIcon className={className} />;
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

const AddItemModal = ({ open, onClose, onSaved, prefilledItem }) => {
  // Lazy-init from prefilled props. Parent supplies a `key` to force remount when prefilledItem changes.
  const [mode, setMode] = useState(prefilledItem?.kind || "file");
  const [title, setTitle] = useState(prefilledItem?.title || "");
  const [description, setDescription] = useState(prefilledItem?.description || "");
  const [category, setCategory] = useState(prefilledItem?.category || "other");
  const [tags, setTags] = useState((prefilledItem?.tags || []).join(", "));
  const [externalUrl, setExternalUrl] = useState(prefilledItem?.external_url || "");
  const [pinned, setPinned] = useState(!!prefilledItem?.pinned);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (mode === "file") {
        // If this is metadata-only edit (existing file, no new file selected) and we have prefilledItem.item_id → PUT
        if (prefilledItem && !file) {
          const r = await fetch(`${API}/instructor-library/${prefilledItem.item_id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              title: title.trim(),
              description: description.trim(),
              category,
              tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
            }),
          });
          if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
          toast.success("Updated");
        } else {
          if (!file && !prefilledItem) { toast.error("Please choose a file"); setSaving(false); return; }
          const fd = new FormData();
          if (file) fd.append("file", file);
          fd.append("title", title.trim());
          fd.append("description", description.trim());
          fd.append("category", category);
          fd.append("tags", tags);
          fd.append("pinned", String(pinned));
          if (prefilledItem) fd.append("replace_item_id", prefilledItem.item_id);
          const r = await fetch(`${API}/instructor-library/upload`, {
            method: "POST",
            credentials: "include",
            body: fd,
          });
          if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
          toast.success(prefilledItem ? "File replaced" : "Material added");
        }
      } else {
        const url = externalUrl.trim();
        if (!url) { toast.error("Please paste a URL"); setSaving(false); return; }
        if (prefilledItem) {
          const r = await fetch(`${API}/instructor-library/${prefilledItem.item_id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              title: title.trim(),
              description: description.trim(),
              category,
              tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
              external_url: url,
            }),
          });
          if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
          toast.success("Updated");
        } else {
          const r = await fetch(`${API}/instructor-library/link`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              title: title.trim(),
              description: description.trim(),
              category,
              tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
              external_url: url,
              pinned,
            }),
          });
          if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
          toast.success("Link added");
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-slate-200 max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Plus className="w-4 h-4 text-brand-navy" />
            {prefilledItem ? "Edit material" : "Add to library"}
          </DialogTitle>
          <DialogDescription className="sr-only">Upload a file or paste an external link.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!prefilledItem && (
            <div className="flex gap-2">
              {[
                { v: "file", label: "Upload File", icon: Upload },
                { v: "link", label: "Add Link", icon: LinkIcon },
              ].map(({ v, label, icon: Icon }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setMode(v)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-semibold transition
                    ${mode === v ? "border-brand-navy bg-brand-navy text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                  data-testid={`library-mode-${v}`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          )}
          <div>
            <Label className="text-slate-600">Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. SOP — Student Onboarding" className="input-base" data-testid="library-title-input" />
          </div>
          <div>
            <Label className="text-slate-600">Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One or two sentences about what's inside…" rows={2} className="input-base" data-testid="library-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-600">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="library-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {LIBRARY_CATEGORIES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-600">Tags (comma-separated)</Label>
              <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="cybersecurity, week1, new" className="input-base" data-testid="library-tags-input" />
            </div>
          </div>
          {mode === "file" ? (
            <div>
              <Label className="text-slate-600">{prefilledItem ? "Replace file (optional)" : "File *"}</Label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.mp4,.txt,.md"
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand-navy file:text-white hover:file:bg-brand-navy/90"
                data-testid="library-file-input"
              />
              <p className="text-xs text-slate-400 mt-1">Max 25 MB. Allowed: PDF, DOCX, XLSX, PPTX, images, MP4, text.</p>
              {prefilledItem && !file && (
                <p className="text-xs text-emerald-700 mt-1">Current file kept. Pick a new one to replace.</p>
              )}
            </div>
          ) : (
            <div>
              <Label className="text-slate-600">External URL *</Label>
              <Input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://docs.google.com/…" className="input-base" data-testid="library-url-input" />
            </div>
          )}
          {!prefilledItem && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="library-pin-checkbox" />
              <Pin className="w-3.5 h-3.5" /> Pin to top
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="library-cancel-btn">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="library-save-btn">
              {saving ? "Saving…" : (<><Save className="w-4 h-4 mr-1" /> Save</>)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ItemCard = ({ item, onOpen, onPin, onEdit, onDelete, isAdmin }) => {
  const cat = LIBRARY_CATEGORIES.find((c) => c.value === item.category) || LIBRARY_CATEGORIES.at(-1);
  const cardClass = `card-surface relative ${item.pinned ? "ring-1 ring-brand-gold/50 border-brand-gold/30" : ""}`;
  return (
    <Card className={cardClass} data-testid={`library-card-${item.item_id}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0
            ${item.kind === "link" ? "bg-violet-100 text-violet-700" : "bg-brand-navy/10 text-brand-navy"}`}>
            <ItemIcon item={item} className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-heading font-bold text-slate-900 text-sm leading-tight line-clamp-2" data-testid={`library-card-title-${item.item_id}`}>
              {item.title}
            </h4>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <Badge className={`text-[10px] ${CATEGORY_COLORS[item.category]}`}>{cat.label}</Badge>
              {item.pinned && (
                <Badge className="text-[10px] bg-brand-gold/20 text-brand-navy border-brand-gold/40 gap-1">
                  <Pin className="w-2.5 h-2.5 fill-current" /> Pinned
                </Badge>
              )}
            </div>
          </div>
        </div>
        {item.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{item.description}</p>}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {item.tags.slice(0, 4).map((t) => (
              <span key={t} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                <Tag className="w-2.5 h-2.5" />{t}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between text-[11px] text-slate-400 mb-3">
          <span className="truncate" title={`${item.uploaded_by_name} · ${formatDate(item.uploaded_at)}`}>
            {item.uploaded_by_name} · {formatDate(item.uploaded_at)}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            <Eye className="w-3 h-3" /> {item.view_count}
            {item.size && <span className="ml-2">· {formatSize(item.size)}</span>}
          </span>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" onClick={() => onOpen(item)} className="flex-1 btn-primary text-xs" data-testid={`library-open-${item.item_id}`}>
            {item.kind === "link" ? (<><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open</>) : (<><Download className="w-3.5 h-3.5 mr-1" /> View</>)}
          </Button>
          <button onClick={() => onPin(item)} className="p-2 rounded hover:bg-slate-100 text-slate-500" title={item.pinned ? "Unpin" : "Pin"} data-testid={`library-pin-${item.item_id}`}>
            {item.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onEdit(item)} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Edit" data-testid={`library-edit-${item.item_id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {isAdmin && (
            <button onClick={() => onDelete(item)} className="p-2 rounded hover:bg-rose-50 text-rose-600" title="Delete (admin only)" data-testid={`library-delete-${item.item_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const InstructorLibraryPanel = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.set("category", category);
      if (search.trim()) params.set("q", search.trim());
      const r = await fetch(`${API}/instructor-library?${params}`, { credentials: "include" });
      if (r.ok) setItems(await r.json());
      else setItems([]);
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, [category, search]);

  useEffect(() => {
    const t = setTimeout(() => fetchItems(), 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleOpen = async (item) => {
    if (item.kind === "link" && item.external_url) {
      window.open(item.external_url, "_blank", "noopener,noreferrer");
      return;
    }
    // For files, fetch via authenticated endpoint and open as blob
    try {
      const r = await fetch(`${API}/instructor-library/${item.item_id}/download`, { credentials: "include" });
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      // Refresh view counts
      fetchItems();
    } catch (e) {
      toast.error(e.message || "Could not open file");
    }
  };

  const handlePin = async (item) => {
    await fetch(`${API}/instructor-library/${item.item_id}/pin`, { method: "POST", credentials: "include" });
    fetchItems();
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone from the UI.`)) return;
    const r = await fetch(`${API}/instructor-library/${item.item_id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { toast.success("Deleted"); fetchItems(); }
    else { toast.error("Delete failed"); }
  };

  const pinned = items.filter((i) => i.pinned);
  const unpinned = items.filter((i) => !i.pinned);

  return (
    <div className="space-y-4" data-testid="instructor-library-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BookMarked className="w-5 h-5 text-brand-navy" />
          <h2 className="font-heading font-bold text-slate-900 text-xl">Instructor Library</h2>
          <Badge className="bg-slate-100 text-slate-600 border-slate-200">{items.length}</Badge>
        </div>
        <Button onClick={() => { setEditingItem(null); setModalOpen(true); }} className="btn-primary gap-1" data-testid="library-add-btn">
          <Plus className="w-4 h-4" /> Add Material
        </Button>
      </div>

      <p className="text-xs text-slate-400 italic">🔒 Admins + instructors only — not visible to participants.</p>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, description, tag…"
            className="pl-9 input-base"
            data-testid="library-search-input"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="library-category-filter">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All categories</SelectItem>
            {LIBRARY_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-500" data-testid="library-empty">
          <BookMarked className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="font-semibold text-slate-700">No materials yet</p>
          <p className="text-xs">Click &quot;Add Material&quot; to upload an SOP, guide, or link.</p>
        </div>
      ) : (
        <>
          {pinned.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Pinned</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pinned.map((it) => (
                  <ItemCard key={it.item_id} item={it} onOpen={handleOpen} onPin={handlePin} onEdit={handleEdit} onDelete={handleDelete} isAdmin={isAdmin} />
                ))}
              </div>
            </div>
          )}
          {unpinned.length > 0 && (
            <div className="space-y-2 mt-6">
              {pinned.length > 0 && <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">All Materials</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {unpinned.map((it) => (
                  <ItemCard key={it.item_id} item={it} onOpen={handleOpen} onPin={handlePin} onEdit={handleEdit} onDelete={handleDelete} isAdmin={isAdmin} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <AddItemModal
        key={editingItem?.item_id || "new-item"}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingItem(null); }}
        onSaved={fetchItems}
        prefilledItem={editingItem}
      />
    </div>
  );
};

export default InstructorLibraryPanel;
